import type {
  DepositEquation,
  DepositRow,
  DepositMovementKind,
} from '@/lib/services/deposits/deposits.types'
import { DetailWell } from '@/components/patterns/detail-page'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Typography } from '@/components/ui/typography'
import { formatMinorCurrency } from './-presentation'

export function DepositBreakdown({
  currency,
  equation,
  movementKind = 'deposit',
}: {
  currency?: DepositRow['currency']
  equation?: Pick<
    DepositEquation,
    | 'collected_minor'
    | 'onestem_fees_minor'
    | 'merchant_fees_minor'
    | 'payout_fees_minor'
    | 'dispute_activity_minor'
    | 'reserve_activity_minor'
    | 'contributions_minor'
    | 'earlier_deposits_minor'
    | 'instant_payouts_minor'
    | 'manual_payouts_minor'
    | 'earlier_bank_withdrawals_minor'
    | 'failed_deposits_returned_minor'
    | 'canceled_deposits_returned_minor'
    | 'uncollected_withdrawals_minor'
    | 'other_activity_minor'
    | 'difference_minor'
    | 'deposit_amount_minor'
    | 'reconciled_net_minor'
  > | null
  movementKind?: DepositMovementKind
}) {
  if (!equation) return null

  const rows = [
    {
      label: 'Card collections',
      amountMinor: equation.collected_minor,
      showWhenZero: movementKind !== 'bank_withdrawal',
    },
    {
      label: 'OneStem fees',
      amountMinor: -Number(equation.onestem_fees_minor ?? 0),
      showWhenZero: true,
    },
    {
      label: 'Merchant fees',
      amountMinor: -Number(equation.merchant_fees_minor ?? 0),
      showWhenZero: true,
    },
    { label: 'Instant Payout fees', amountMinor: -Number(equation.payout_fees_minor ?? 0) },
    { label: 'Disputes', amountMinor: equation.dispute_activity_minor, showPositive: true },
    {
      label: 'Funds held or released',
      amountMinor: equation.reserve_activity_minor,
      showPositive: true,
    },
    { label: 'Contributions', amountMinor: equation.contributions_minor, showPositive: true },
    {
      label: 'Deposited earlier',
      amountMinor: equation.earlier_deposits_minor,
      showPositive: true,
    },
    { label: 'Instant Payouts', amountMinor: equation.instant_payouts_minor, showPositive: true },
    { label: 'Manual payouts', amountMinor: equation.manual_payouts_minor, showPositive: true },
    {
      label: 'Collected from your bank earlier',
      amountMinor: equation.earlier_bank_withdrawals_minor,
      showPositive: true,
    },
    {
      label: 'From an earlier failed deposit',
      amountMinor: equation.failed_deposits_returned_minor,
      showPositive: true,
    },
    {
      label: 'From an earlier canceled deposit',
      amountMinor: equation.canceled_deposits_returned_minor,
      showPositive: true,
    },
    {
      label: 'Uncollected bank withdrawal',
      amountMinor: equation.uncollected_withdrawals_minor,
      showPositive: true,
    },
    {
      label: 'Other account activity',
      amountMinor: equation.other_activity_minor,
      showPositive: true,
    },
  ].filter((row) => row.showWhenZero || Number(row.amountMinor) !== 0)
  const hasDifference = Number(equation.difference_minor) !== 0

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {movementKind === 'bank_withdrawal' ? 'Withdrawal breakdown' : 'Deposit breakdown'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="space-y-3">
          {rows.map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-4">
              <Typography as="dt" variant="supporting">
                {row.label}
              </Typography>
              <Typography as="dd" variant="labelStrong" className="tabular-nums">
                {formatContribution(row.amountMinor, currency, row.showPositive)}
              </Typography>
            </div>
          ))}
          <Separator />
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4">
            <Typography as="dt" variant="labelStrong">
              {movementKind === 'bank_withdrawal' ? 'Withdrawal total' : 'Deposit total'}
            </Typography>
            <Typography as="dd" variant="h4" className="tabular-nums">
              {formatMinorCurrency(equation.deposit_amount_minor, currency)}
            </Typography>
          </div>
        </dl>

        {hasDifference ? (
          <DetailWell tone="warning">
            <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-2">
              <Typography as="dt" variant="label">
                Calculated total
              </Typography>
              <Typography as="dd" variant="labelStrong" className="tabular-nums">
                {formatMinorCurrency(equation.reconciled_net_minor, currency)}
              </Typography>
              <Typography as="dt" variant="label">
                Difference
              </Typography>
              <Typography as="dd" variant="labelStrong" className="tabular-nums">
                {formatContribution(equation.difference_minor, currency, true)}
              </Typography>
            </dl>
          </DetailWell>
        ) : null}
      </CardContent>
    </Card>
  )
}

function formatContribution(
  amount: number | null | undefined,
  currency: DepositRow['currency'] | undefined,
  showPositive = false
): string {
  const numericAmount = Number(amount ?? 0)
  const formatted = formatMinorCurrency(Math.abs(numericAmount), currency)

  if (numericAmount < 0) return `−${formatted}`
  if (showPositive && numericAmount > 0) return `+${formatted}`

  return formatted
}
